// Тонкий лоадер Stockfish WASM для Mini App.
//
// Почему свой лоадер, а не @ext/engine/engine.ts напрямую: engine.ts резолвит
// путь к .wasm через chrome.runtime.getURL / import.meta.url ('../vendor/…'),
// что в vite-бандле Mini App (dev и build) указывает НЕ на public/vendor.
// Здесь путь к .wasm явный и абсолютный ('/vendor/stockfish/…', public/ отдаётся
// из корня). UCI-контракт (AnalyzeOptions, MOVETIME_DEFAULT_MS, EngineAnalysis)
// переиспользуется из ядра расширения — не форкаем.
import { type AnalyzeOptions } from '@ext/engine/engine';
import type { EngineAnalysis } from '@ext/core/eval-utils';

const SCRIPT_ID = 'stockfishScript';
const VENDOR = '/vendor/stockfish/';
const SCRIPT_FILE = 'stockfish-18-lite-single.js';
const WASM_FILE = 'stockfish-18-lite-single.wasm';
const INIT_TIMEOUT_MS = 20000;
const SEARCH_TIMEOUT_MS = 45000;

interface StockfishModule {
  postMessage?: (command: string) => void;
  processCommand?: (command: string) => void;
  ccall?: (name: string, ret: null, types: string[], args: string[], opts?: { async?: boolean }) => void;
  terminate?: () => void;
  worker?: { terminate?: () => void };
}
type StockfishFactory = (opts: { locateFile(path: string): string; listener(line: string): void }) => Promise<StockfishModule>;

interface Engine {
  post(cmd: string): void;
  on(cb: (line: string) => void): () => void;
}

let enginePromise: Promise<Engine> | null = null;
let queue: Promise<unknown> = Promise.resolve();

/** Загрузить вендорный скрипт лениво → globalThis.Stockfish (или el._exports). */
function loadScript(): Promise<StockfishFactory> {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as (HTMLElement & { _exports?: unknown }) | null;
    const pick = (el: HTMLElement | null) => {
      const factory = (el as { _exports?: unknown } | null)?._exports || (globalThis as Record<string, unknown>).Stockfish;
      if (typeof factory === 'function') return factory as StockfishFactory;
      return null;
    };
    const ready = pick(existing);
    if (ready) return resolve(ready);
    const el = existing || document.createElement('script');
    el.id = SCRIPT_ID;
    el.addEventListener('load', () => {
      const f = pick(el);
      if (f) resolve(f);
      else reject(new Error('Stockfish factory missing after load'));
    });
    el.addEventListener('error', () => reject(new Error('failed to load Stockfish script')));
    if (!existing) {
      (el as HTMLScriptElement).src = VENDOR + SCRIPT_FILE;
      document.head.appendChild(el);
    }
  });
}

function post(module: StockfishModule, cmd: string): void {
  if (typeof module.postMessage === 'function') module.postMessage(cmd);
  else if (typeof module.processCommand === 'function') module.processCommand(cmd);
  else if (typeof module.ccall === 'function') module.ccall('command', null, ['string'], [cmd], { async: /^go\b/.test(cmd) });
  else throw new Error('Stockfish does not accept UCI commands');
}

async function initEngine(): Promise<Engine> {
  const factory = await loadScript();
  const listeners = new Set<(line: string) => void>();
  const module = await factory({
    locateFile: (p) => (p.endsWith('.wasm') ? VENDOR + WASM_FILE : VENDOR + p),
    listener: (line) => { for (const cb of listeners) cb(String(line)); },
  });
  const engine: Engine = {
    post: (cmd) => post(module, cmd),
    on: (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
  };
  await waitFor(engine, 'uci', /^uciok$/);
  engine.post('setoption name Threads value 1');
  engine.post('setoption name Hash value 32'); // мобильная ручка из PLAN-V2
  await waitFor(engine, 'isready', /^readyok$/);
  return engine;
}

function ensureEngine(): Promise<Engine> {
  if (!enginePromise) {
    const p = initEngine().catch((e) => { if (enginePromise === p) enginePromise = null; throw e; });
    enginePromise = p;
  }
  return enginePromise;
}

function waitFor(engine: Engine, cmd: string, pattern: RegExp): Promise<void> {
  return new Promise((resolve, reject) => {
    const off = engine.on((line) => {
      if (!pattern.test(line.trim())) return;
      clearTimeout(timer); off(); resolve();
    });
    const timer = setTimeout(() => { off(); reject(new Error(`Stockfish timeout: ${cmd}`)); }, INIT_TIMEOUT_MS);
    engine.post(cmd);
  });
}

/** true если движок поднимается (для фолбэка UI). */
export async function engineHealthy(): Promise<boolean> {
  try { await ensureEngine(); return true; } catch (e) { console.warn('Stockfish init failed', e); return false; }
}

/** fen → анализ; вызовы сериализуются (движок один). Совместимо с AnalyzeFn ядра. */
export function analyze(fen: string, opts: AnalyzeOptions = {}): Promise<EngineAnalysis> {
  const run = () => analyzeOne(fen, opts);
  const next = queue.then(run, run);
  queue = next.catch(() => {});
  return next;
}

interface Info { multipv: number; depth: number | null; cp: number | null; mate: number | null; pv: string[] }

async function analyzeOne(fen: string, { depth, multipv, movetimeMs }: AnalyzeOptions): Promise<EngineAnalysis> {
  const engine = await ensureEngine();
  const maxPv = Math.max(1, Math.min(5, Number(multipv) || 1));
  const searchDepth = Math.max(1, Math.min(32, Number(depth) || 14));
  const movetime = Math.max(200, Math.min(120000, Number(movetimeMs) || 1200));
  const infos = new Map<number, Info>();
  return new Promise((resolve, reject) => {
    let settled = false;
    const off = engine.on((line) => {
      const text = line.trim();
      if (text.startsWith('info ')) { const p = parseInfo(text); if (p) infos.set(p.multipv, p); return; }
      if (text.startsWith('bestmove ')) finish(() => resolve(build(text, infos)));
    });
    const timer = setTimeout(() => {
      try { engine.post('stop'); } catch { /* best effort */ }
      finish(() => reject(new Error('Stockfish timeout: bestmove')));
    }, Math.max(SEARCH_TIMEOUT_MS, movetime + 10000));
    function finish(done: () => void): void {
      if (settled) return; settled = true; clearTimeout(timer); off(); done();
    }
    try {
      engine.post(`setoption name MultiPV value ${maxPv}`);
      engine.post(`position fen ${fen}`);
      engine.post(`go depth ${searchDepth} movetime ${movetime}`);
    } catch (e) { finish(() => reject(e as Error)); }
  });
}

export function parseInfo(line: string): Info | null {
  const pvAt = line.indexOf(' pv ');
  if (pvAt < 0) return null;
  const head = line.slice(0, pvAt).trim().split(/\s+/);
  const pv = line.slice(pvAt + 4).trim().split(/\s+/).filter(Boolean);
  if (!pv.length) return null;
  const info: Info = { multipv: 1, depth: null, cp: null, mate: null, pv };
  for (let i = 0; i < head.length; i++) {
    if (head[i] === 'depth') info.depth = num(head[++i]);
    else if (head[i] === 'multipv') info.multipv = num(head[++i]) || 1;
    else if (head[i] === 'score' && head[i + 1] === 'cp') { info.cp = num(head[i + 2]); info.mate = null; i += 2; }
    else if (head[i] === 'score' && head[i + 1] === 'mate') { info.mate = num(head[i + 2]); info.cp = null; i += 2; }
  }
  return info.cp == null && info.mate == null ? null : info;
}

function num(v: string | undefined): number | null { const n = Number(v); return Number.isFinite(n) ? n : null; }

export function build(bestmoveLine: string, infos: Map<number, Info>): EngineAnalysis {
  const parts = bestmoveLine.split(/\s+/);
  const bestmove = parts[1] && parts[1] !== '(none)' ? parts[1] : null;
  const ponderIdx = parts.indexOf('ponder');
  const ponder = ponderIdx >= 0 ? parts[ponderIdx + 1] || null : null;
  const sorted = [...infos.entries()].sort((a, b) => a[0] - b[0]).map(([, i]) => i);
  const lines = sorted.map((i) => ({ move: i.pv[0] || null, cp: i.cp, mate: i.mate, pv: i.pv }));
  const top = sorted[0] || { cp: null, mate: null, pv: bestmove ? [bestmove] : [], depth: null };
  return { bestmove, ponder, cp: top.cp, mate: top.mate, pv: top.pv || (bestmove ? [bestmove] : []), depth: top.depth, lines };
}
